import React, { useSyncExternalStore } from "react";
import { APIProvider, Map as ReactMap, AdvancedMarker } from "@vis.gl/react-google-maps";
import { MdOutlineWifiOff } from "react-icons/md";
import { IoPowerSharp } from "react-icons/io5";
import oc from "open-color";
import { ObjectState, ObjectStateCaclulator } from "./object-state";
import { BiError } from "react-icons/bi";

export interface MarkerProps {
  coordinates?: [lat: number, lng: number];
  subscribeToVisData: (callback: () => void) => () => void;
  getVisDataSnapshot: () => Readonly<Record<string, string>>;
  name: string;
  onClick?: () => void;
  stateCalculator: ObjectStateCaclulator;
}

export default function MapWithMarkers({
  mapApiKey,
  mapCoordinates,
  markersData,
  colors,
  defaultZoom,
}: {
  mapApiKey: string;
  mapCoordinates: [lat: number, lng: number];
  markersData: MarkerProps[];
  colors: Record<ObjectState, string>;
  defaultZoom?: number;
}): React.ReactElement {
  const markers = markersData.map((marker, i) => (
    <Marker key={i} marker={marker} colors={colors} />
  ));
  return (
    <APIProvider apiKey={mapApiKey}>
      <ReactMap
        mapId="vis-dashboard-map"
        style={{ width: "100%", height: "600px" }}
        defaultZoom={defaultZoom || 11}
        defaultCenter={{ lat: mapCoordinates[0], lng: mapCoordinates[1] }}
      >
        {markers}
      </ReactMap>
    </APIProvider>
  );
}

function Marker({
  marker,
  colors,
}: {
  marker: MarkerProps;
  colors: Record<ObjectState, string>;
}): React.ReactNode {
  const coordinates = marker.coordinates;
  if (!coordinates) {
    return null;
  }

  const data = useSyncExternalStore(marker.subscribeToVisData, marker.getVisDataSnapshot);

  const markerState = marker.stateCalculator(data);
  const color = colors[markerState];

  return (
    <AdvancedMarker
      position={{ lat: coordinates[0], lng: coordinates[1] }}
      clickable
      onClick={() => marker.onClick && marker.onClick()}
    >
      <div
        style={{
          marginLeft: "50%",
          borderLeft: `2px solid ${oc.blue[8]}`,
          paddingBottom: "15px",
          minWidth: "125px",
          whiteSpace: "nowrap",
          width: "fit-content",
          maxWidth: "300px",
        }}
      >
        <div
          style={{
            display: "flex",
            backgroundColor: oc.white,
            padding: "5px",
            alignItems: "flex-start",
          }}
        >
          <div style={{ marginRight: "7px" }}>
            {markerState === "no-connection" ? (
              <MdOutlineWifiOff color={color} size={22} />
            ) : markerState === "error" ? (
              <BiError color={color} size={22} />
            ) : (
              <IoPowerSharp color={color} size={22} />
            )}
          </div>
          <div
            style={{
              color: oc.gray[9],
              fontSize: "16px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {marker.name}
          </div>
        </div>
      </div>
    </AdvancedMarker>
  );
}
